import { storeAuthReturn } from "../utils/authReturn";
import { safeLocalStorage } from "../utils/safeLocalStorage";
import { needsMemberMutationKey, newMutationKey } from "../util/requestIdempotency";
import axios, { AxiosHeaders, AxiosRequestConfig, AxiosResponse, CanceledError } from "axios";
import {
    captureAccessTicket,
    accessTicketIsCurrent,
    offlineAccessAllowed,
    resourceAccessState,
    protectedCacheAuthority,
    type AccessTicket,
} from "../access/resourceAccess";
import { observeResponseAdmission } from "../access/responseAdmission";
import { performLogout } from "../utils/logout";
import { isInIframe } from "../utils/browserUtils";
import { isDebugEnabled } from "../hooks/configuration";
import { getPlatform } from "../platform/platformRef";
import { shellHoldsSession } from "../platform/shellSession";
import type { ConnectivityObservation, OfflineRequestConfig } from "../platform/Platform";
import { isDefinitiveAuthRejection } from "../shared/authRejectionClassifier";
import { EMBED_AUTH_PATH } from "./embedConfig";
import { isE2E } from "../platform/e2e";
import { installE2ELedgerInterceptors } from "./axiosE2ELedger";
import { assertApiOrigin } from "./apiOrigin";

import {
    currentTabCompanySession,
    expireTabCompanySession,
    renewForDisabledRoute,
    revalidateTenantSession,
    tabCompanySessionJwt,
} from "../contexts/tabCompanySession";
import {
    applyStaffProjectRoute,
    applyTenantHeaderPolicy,
    closeTenantScopeForDocument,
    isTenantScopeDocumentClosed,
    TenantScopeUnavailableError,
    assertTenantRequestScope,
    captureTenantScopeGeneration,
    isTenantScopeRequired,
    isUnvalidatedTenantRequestAllowed,
    isSourceCredentialRequest,
    releaseScopeRequestSignal,
    scopeRequestSignal,
} from "../contexts/tenantScopeHeaders";
import { markProjectRefused, projectRefusalOf } from "../contexts/projectRefusal";
import { applyClientDeclarationHeaders } from "../api/clientDeclaration";
import { forwardAppUpdate } from "../api/appUpdate";
import { isRouteDisabled } from "../utils/admissionRefusal";
import { isTokenPage } from "../util/routePath";

type AccessRequest = AxiosRequestConfig & {
    _accessTicket?: AccessTicket;
    /** The account the request goes out as or not at all (`sentOnlyAs`). */
    _sendOnlyAs?: string | null;
    /** Answered for another Document, not this tab (`detachedFromTab`). */
    _detached?: boolean;
};

/**
 * Config for an admission request whose answer this tab never uses: the switcher's prefetch of
 * another project's admission, handed to the Document that opens it. It goes out like any
 * admission request, with the source credential and through the same fences, but its answer,
 * a refusal included, is not observed as this tab's access, and none of this tab's recovery
 * runs on it: no lease rejection, renewal, sign-in redirect or reload.
 */
export function detachedFromTab(): AxiosRequestConfig {
    const config: AccessRequest = { _detached: true };
    return config;
}

/**
 * Config for a request that goes out as the account `scope` names (`captureAccessTicket().scope`)
 * or not at all. The interceptor stamps identity after the call returns, and a sign-in can land
 * in between (`TenantQueryClientProvider` unmounts the viewer, whose cleanup starts its last
 * save, in the commit whose effect sets the new scope), so it checks where it stamps the ticket
 * and again once it has read the token.
 */
export function sentOnlyAs(scope: string | null): AxiosRequestConfig {
    const config: AccessRequest = { _sendOnlyAs: scope };
    return config;
}

/** `sentOnlyAs` the account signed in now: a command keeps its issuer through every resend. */
export function sentAsSignedIn(): AxiosRequestConfig {
    return sentOnlyAs(captureAccessTicket().scope);
}

/** A `sentOnlyAs` request is stamped as its account, which is still the one signed in. */
function stillSendsAsItsAccount(request: AccessRequest): boolean {
    const account = request._sendOnlyAs;
    return (
        account === undefined ||
        (request._accessTicket?.scope === account && captureAccessTicket().scope === account)
    );
}

const ANOTHER_ACCOUNT_SIGNED_IN = "Another account signed in before this request went out";

const STORAGE_KEY_JWT = "primepoint_embed_session_jwt";
const STORAGE_KEY_TOKEN = "primepoint_embed_session_token";
const STORAGE_KEY_EXP = "primepoint_embed_session_exp";

function loadSessionJwtFromStorage(): string | null {
    if (typeof window === "undefined") {
        return null;
    }

    let jwt: string | null;
    try {
        jwt = localStorage.getItem(STORAGE_KEY_JWT);
    } catch {
        return null;
    }
    if (!jwt) {
        return null;
    }

    let expStr: string | null;
    try {
        expStr = localStorage.getItem(STORAGE_KEY_EXP);
    } catch {
        return null;
    }
    if (expStr) {
        const exp = Number.parseInt(expStr, 10);
        const now = Math.floor(Date.now() / 1000);

        if (!Number.isNaN(exp) && exp <= now) {
            return null;
        }
    }
    return jwt;
}

let getEmbedTokens: (() => { sessionJwt: string; exp: number } | null) | null = null;
let refreshEmbedTokens: (() => Promise<void>) | null = null;
// Bootstrap sets this before React mounts. EmbedAuthProvider updates it when
// the active session changes.
let isEmbedAuthEnabled = false;

/** A host whose shell holds the session (desktop's Rust auth, iOS's Keychain) publishes
 *  `getSession`; the web's cookie session has none to restore. */
const PROACTIVE_REFRESH_THRESHOLD_SECONDS = 60;
const NATIVE_AUTH_BOOTSTRAP_WAIT_MS = 10_000;
let authRedirectStarted = false;

type EmbedSessionJwt = { sessionJwt: string; exp: number };
type NativeAuthBootstrapHandler = () => Promise<EmbedSessionJwt | null>;

let nativeAuthBootstrapHandler: NativeAuthBootstrapHandler | null = null;
let nativeAuthBootstrapPromise: Promise<EmbedSessionJwt | null> | null = null;
let nativeAuthBootstrapSessionIsValid = true;

/**
 * Register the native SDK session restoration before the provider renders its
 * children. The promise itself is created lazily and shared by every request,
 * so a cold WebView boot can never fan out concurrent Keychain reads.
 */
export function setNativeAuthBootstrapHandler(handler: NativeAuthBootstrapHandler | null): void {
    nativeAuthBootstrapHandler = handler;
    if (handler === null) {
        nativeAuthBootstrapPromise = null;
        nativeAuthBootstrapSessionIsValid = true;
    }
}

function nativeAuthBootstrap(): Promise<EmbedSessionJwt | null> | null {
    if (!shellHoldsSession() || !nativeAuthBootstrapHandler) {
        return null;
    }
    if (!nativeAuthBootstrapPromise) {
        nativeAuthBootstrapPromise = nativeAuthBootstrapHandler();
    }
    return nativeAuthBootstrapPromise;
}

/** Starts the shell's session restore if a handler is registered (`setNativeAuthBootstrapHandler`)
 *  and returns it; null while none is registered. Null means "not yet known", never "signed
 *  out": a provider's effect replay unregisters the handler until its next render. */
export function startNativeAuthBootstrap(): Promise<EmbedSessionJwt | null> | null {
    return nativeAuthBootstrap();
}

/** Await the full native restoration lifecycle for native auth UI. */
export async function waitForNativeAuthBootstrap(): Promise<EmbedSessionJwt | null> {
    const restored = (await nativeAuthBootstrap()) ?? null;
    return nativeAuthBootstrapSessionIsValid ? restored : null;
}

/** Do not allow a boot-time JWT to be reused after logout or account removal. */
export function invalidateNativeAuthBootstrapSession(): void {
    nativeAuthBootstrapSessionIsValid = false;
}

async function waitBrieflyForNativeAuthBootstrap(): Promise<EmbedSessionJwt | null> {
    const bootstrap = nativeAuthBootstrap();
    if (!bootstrap) {
        return null;
    }

    let timeoutId: number | undefined;
    const timeout = new Promise<never>((_resolve, reject) => {
        timeoutId = window.setTimeout(() => {
            reject(
                Object.assign(new Error("Native authentication is still initializing"), {
                    code: "NATIVE_AUTH_INITIALIZING",
                }),
            );
        }, NATIVE_AUTH_BOOTSTRAP_WAIT_MS);
    });

    try {
        const restored = await Promise.race([bootstrap, timeout]);
        return nativeAuthBootstrapSessionIsValid ? restored : null;
    } finally {
        if (timeoutId !== undefined) {
            window.clearTimeout(timeoutId);
        }
    }
}

function clearStoredEmbedTokens() {
    safeLocalStorage.removeItem(STORAGE_KEY_JWT);
    safeLocalStorage.removeItem(STORAGE_KEY_TOKEN);
    safeLocalStorage.removeItem(STORAGE_KEY_EXP);
}

async function maybeRefreshEmbedTokens(): Promise<void> {
    if (!isEmbedAuthEnabled || !getEmbedTokens || !refreshEmbedTokens) {
        return;
    }

    const tokenData = getEmbedTokens();
    if (!tokenData) {
        return;
    }

    const now = Math.floor(Date.now() / 1000);
    const secondsUntilExpiry = tokenData.exp - now;

    if (secondsUntilExpiry > PROACTIVE_REFRESH_THRESHOLD_SECONDS) {
        return;
    }

    if (isDebugEnabled())
        console.log("[axios] Token expires soon, refreshing proactively before request");
    await refreshEmbedTokens();
    if (isDebugEnabled()) console.log("[axios] Proactive refresh successful");
}

export async function getEmbedSessionJwt(sourceCredential = false): Promise<string | null> {
    if (!sourceCredential && currentTabCompanySession()) return tabCompanySessionJwt();
    // A project URL tab's admission requests send the stored source JWT as it is: ai_engine
    // authenticates that session itself and hands back the provider's fresh JWT, so no client
    // refresh, however slow, can hold up a renewal. The embed's own schedule still refreshes it.
    if (isEmbedAuthEnabled && !isTenantScopeRequired()) {
        try {
            await maybeRefreshEmbedTokens();
        } catch (error) {
            if (error instanceof TenantScopeUnavailableError) throw error;
            if (isDebugEnabled())
                console.error("[axios] Proactive refresh failed, proceeding with request", error);
        }
    }

    let sessionJwt: string | null = null;

    if (isEmbedAuthEnabled && getEmbedTokens) {
        sessionJwt = getEmbedTokens()?.sessionJwt ?? null;
    }

    if (!sessionJwt && isEmbedAuthEnabled) {
        sessionJwt = loadSessionJwtFromStorage();
    }

    if (!sessionJwt && isEmbedAuthEnabled && shellHoldsSession()) {
        try {
            const restored = await waitBrieflyForNativeAuthBootstrap();
            const now = Math.floor(Date.now() / 1000);
            if (restored && restored.exp > now) {
                sessionJwt = restored.sessionJwt;
            }
        } catch (error) {
            if (isDebugEnabled()) {
                console.error(
                    "[axios] Native auth bootstrap did not complete before request",
                    error,
                );
            }
            throw error;
        }
    }

    return sessionJwt;
}

export async function recoverFromEmbedAuthFailure(alreadyRetried: boolean): Promise<boolean> {
    if (isTenantScopeDocumentClosed()) return false;
    if (currentTabCompanySession()) {
        if (alreadyRetried) return false;
        // A server 401 outranks the local deadline: renew as an expired credential, so a
        // failed renewal shows the outage instead of resending the refused JWT.
        expireTabCompanySession();
        await revalidateTenantSession();
        return true;
    }
    if (!alreadyRetried && refreshEmbedTokens) {
        if (isDebugEnabled()) console.log("[axios] Attempting reactive token refresh after 401...");

        try {
            await refreshEmbedTokens();
            if (isDebugEnabled())
                console.log("[axios] Reactive refresh successful, retrying original request");
            return true;
        } catch (refreshError) {
            // A superseded request cannot revoke the account that replaced it.
            if (
                refreshError instanceof TenantScopeUnavailableError ||
                isTenantScopeDocumentClosed()
            )
                return false;
            if (isDebugEnabled()) console.error("[axios] Reactive refresh failed", refreshError);
            if (!isDefinitiveAuthRejection(refreshError)) {
                // Preserve the session (a durable native one, or an embed panel's
                // tokens) and cached UI during a transport/backend outage. A later
                // refresh can recover it without converting a network fault into logout.
                return false;
            }
        }
    } else {
        if (isDebugEnabled())
            console.log("[axios] Not retrying (already retried or no refresh handler)", {
                retry: alreadyRetried,
                hasHandler: !!refreshEmbedTokens,
            });
    }

    if (isOwnAuthFlowPage()) {
        return false;
    }

    if (authRedirectStarted) {
        return false;
    }
    authRedirectStarted = true;

    if (isDebugEnabled()) console.log("[axios] Clearing tokens and redirecting to /authenticate");
    const { auth, kind } = getPlatform();
    if (kind === "ios" && auth.logout) {
        // A native session survives WebView reloads. Revoke it before the
        // redirect. Keep offline data for this silent recovery.
        await auth.logout({ wipeOfflineData: false }).catch(() => undefined);
    }
    await performLogout();
    clearStoredEmbedTokens();
    window.location.replace("/authenticate");
    return false;
}

// Pages that run their own auth flow must never be hard-redirected to
// /authenticate on a 401: the /embed-auth popup's Stytch discovery flow
// revokes the current session mid-flight (so background requests 401 by
// design), and yanking the popup away before it hands tokens to the opener
// iframe loops the Procore sign-in forever.
function isOwnAuthFlowPage(): boolean {
    return (
        window.location.pathname.startsWith("/authenticate") ||
        // The outside-party guest page authenticates with its own one-use token
        // and never has a Stytch session, and a product update email's stop page is
        // signed out too. A 401 from any incidental request must not log either out
        // and bounce it to /authenticate, and the redirect target would also persist
        // the token into localStorage.
        isTokenPage(window.location.pathname) ||
        window.location.pathname === EMBED_AUTH_PATH
    );
}

export function setEmbedAuthEnabled(enabled: boolean) {
    isEmbedAuthEnabled = enabled;
}

export function setEmbedTokenProvider(provider: () => { sessionJwt: string; exp: number } | null) {
    getEmbedTokens = provider;
}

export function setEmbedRefreshHandler(handler: () => Promise<void>) {
    refreshEmbedTokens = handler;
}

function stableSerialize(value: unknown): string {
    if (value === null || typeof value !== "object") return JSON.stringify(value);
    if (value instanceof Date) return JSON.stringify(value.toISOString());
    if (value instanceof URLSearchParams) {
        return JSON.stringify(Array.from(value.entries()).sort(([a], [b]) => a.localeCompare(b)));
    }
    if (Array.isArray(value)) return `[${value.map(stableSerialize).join(",")}]`;
    return `{${Object.entries(value as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, item]) => `${JSON.stringify(key)}:${stableSerialize(item)}`)
        .join(",")}}`;
}

/**
 * Paths whose RESPONSE depends on who is asking, not only on which tenant.
 *
 * The offline cache keys on tenant + URL + params, with no role or member segment, so a
 * response that varies by caller is not cacheable here at all: an admin populating the entry
 * hands it to the next person in the same org who hits a 5xx and takes the fallback. For
 * clash alerts that payload carries unpublished and rejected findings, reviewer identities
 * and review notes — the server-side filter is the boundary and it holds, but this cache
 * reaches around it. File comparisons are private-by-default per creator, so the same risk
 * applies: one member's cached list/detail response must never answer another member's
 * request during a fallback. The IndexedDB React Query cache does not need the same
 * exclusion — it already rotates by organization, API base, and member ID, and File
 * Comparison queries are not persisted there by default — but this native store rotates
 * by tenant only, so it needs telling explicitly. The bell's notifications are the
 * caller's own (titles, revision IDs, attention rows): one member's list must never
 * answer the next member's failed request either. The find box's index and quick search
 * carry only what the caller may see.
 */
const ROLE_DEPENDENT_PATHS = [
    "/api/ai/clash-alerts",
    "/api/ai/file-comparisons",
    "/api/ai/directory",
    "/api/ai/project-access",
    "/api/ai/notifications",
    "/api/ai/ops/miranda-runs",
    "/api/ai/ops/marvin-eval-runs",
    "/api/ai/ops/precon-ontology",
    // The staff Admin console and Usage: platform-wide reads for staff only.
    "/api/ai/ops/console",
    "/api/ai/ops/usage",
    "/api/ai/search/",
];

/** The API URL a request the transport's offline paths apply to (the replay cache,
 *  connectivity observations, answer-first): null on a host with no offline capability.
 *  Prefs alone, which every host has, never answer a request. */
function nativeApiUrl(config: AxiosRequestConfig): URL | null {
    // A browser areas repository is an explicit family owner; it must not turn
    // on the legacy generic HTTP replay cache for every other request.
    const offline = getPlatform().offline;
    if (!offline?.answer && !offline?.connectivity) return null;
    try {
        const base = config.baseURL ?? window.location.origin;
        const url = new URL(config.url ?? "", base);
        return url.pathname.startsWith("/api/") ? url : null;
    } catch {
        return null;
    }
}

/** A staff read routed to another project (`_projectRoute`): never replayed from, or written to, this device's
 *  offline stores, which belong to the signed-in organization, not to the project the request names. */
function isStaffProjectRouted(config: AxiosRequestConfig): boolean {
    return !!(config as StaffProjectRouteConfig)._projectRoute;
}

function nativeGetCacheKey(config: AxiosRequestConfig): string | null {
    if (isTenantScopeRequired() || isStaffProjectRouted(config)) return null;
    if ((config.method ?? "get").toLowerCase() !== "get") return null;
    if (config.responseType && config.responseType !== "json" && config.responseType !== "text") {
        return null;
    }
    try {
        const url = nativeApiUrl(config);
        if (!url) return null;
        if (ROLE_DEPENDENT_PATHS.some((path) => url.pathname.startsWith(path))) return null;
        if (url.pathname === "/api/ai/areas" && getPlatform().offline?.areas) return null;
        const suffix = `${url.origin}${url.pathname}${url.search}|${stableSerialize(config.params ?? null)}`;
        // The replay store is keyed by tenant, API origin and reader (`prefs.tenantKey`),
        // and cleared at logout. On a protected project the key also names the admitted
        // authority, so a response is replayed only to the role and policy version that
        // received it (D8).
        const authority = protectedCacheAuthority();
        return getPlatform().prefs.tenantKey(
            "http",
            authority ? `authority:${authority}|${suffix}` : suffix,
        );
    } catch {
        return null;
    }
}

export type NativeOfflineRequestConfig = OfflineRequestConfig;

/**
 * True for a transport failure (no response) or a 5xx -- the same "worth an offline
 * fallback / worth a durable retry" rule this interceptor uses for its own cache, and
 * the one the markup outbox uses to decide
 * whether a failed save/delete is a connectivity problem (queue it) or a real rejection
 * (4xx -- surface it, never queue).
 */
export function shouldUseOfflineFallback(error: { response?: { status?: number } }): boolean {
    const status = error.response?.status;
    return status === undefined || status >= 500;
}

function redactAuthorizationHeader(config: { headers?: unknown } | undefined): void {
    const headers = config?.headers;
    if (headers instanceof AxiosHeaders) {
        headers.delete("Authorization");
        return;
    }
    if (!headers || typeof headers !== "object") {
        return;
    }
    for (const key of Object.keys(headers)) {
        if (key.toLowerCase() === "authorization") {
            delete (headers as Record<string, unknown>)[key];
        }
    }
}

function redactAuthFromRejectedError(error: {
    config?: { headers?: unknown };
    response?: { config?: { headers?: unknown } };
}): void {
    // This runs only after reactive refresh/retry and offline fallback have
    // declined the request. Until then the request config must retain the
    // bearer header so Axios can actually replay it.
    redactAuthorizationHeader(error.config);
    if (error.response?.config !== error.config) {
        redactAuthorizationHeader(error.response?.config);
    }
}

function publishConnectivityObservation(observation: ConnectivityObservation): void {
    getPlatform().offline?.connectivity?.observe(observation);
}

const SAFE_RETRY_DETAILS = new Set([
    "tenant_unavailable",
    "lock_conflict",
    "project_route_unavailable",
]);
const IDEMPOTENT_METHODS = new Set(["get", "head", "options"]);
const DEFAULT_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 5000;

/** ai_engine answers 503 with one of these bodies for a condition that clears on its own (a
 *  tenant handoff, or another session holding the rows). It does not promise that nothing was
 *  written: a route may have committed before failing, so only an idempotent read is resent. */
export function isSafeRetryable503(error: {
    config?: { method?: string };
    response?: { status?: number; data?: { detail?: unknown } };
}): boolean {
    const detail = error.response?.data?.detail;
    return (
        error.response?.status === 503 &&
        typeof detail === "string" &&
        SAFE_RETRY_DETAILS.has(detail) &&
        IDEMPOTENT_METHODS.has((error.config?.method ?? "get").toLowerCase())
    );
}

/** Resolves after ``ms``, or as soon as ``signal`` aborts. */
function abortableDelay(ms: number, signal?: AbortSignal | null): Promise<void> {
    return new Promise((resolve) => {
        if (signal?.aborted) return resolve();
        const done = () => {
            clearTimeout(timer);
            signal?.removeEventListener("abort", done);
            resolve();
        };
        const timer = setTimeout(done, ms);
        signal?.addEventListener("abort", done, { once: true });
    });
}

/** Retry-After in seconds, capped at 5 s; 1 s when absent or not a number. */
export function safeRetryDelayMs(retryAfter: unknown): number {
    const seconds = typeof retryAfter === "string" ? Number(retryAfter.trim()) : Number.NaN;
    if (retryAfter === "" || !Number.isFinite(seconds) || seconds < 0)
        return DEFAULT_RETRY_DELAY_MS;
    return Math.min(seconds * 1000, MAX_RETRY_DELAY_MS);
}

/** A request a staff page sends to one project's own routes (only `api/staffProjectRoute.ts` sets it). */
export type StaffProjectRouteConfig = { _projectRoute?: string };

let axiosInterceptorsInstalled = false;

export function installAxiosInterceptors(): void {
    const platform = getPlatform();
    axios.defaults.withCredentials = platform.auth.withCredentials;
    isEmbedAuthEnabled =
        shellHoldsSession() || isInIframe() || localStorage.getItem(STORAGE_KEY_TOKEN) !== null;
    if (axiosInterceptorsInstalled) return;
    axiosInterceptorsInstalled = true;
    // First, so its request half runs last (LIFO) and its response half runs first.
    if (isE2E()) installE2ELedgerInterceptors(axios);

    // Request interceptor: Add Authorization header if embed tokens available
    // Proactively refresh tokens if they're about to expire
    axios.interceptors.request.use(
        async (config) => {
            // Capture both ownership fields before the first await. Axios reuses
            // these private fields for a reactive-auth retry, so a request that
            // began before logout can never relabel its eventual response with the
            // replacement member's same-org namespace or prefs generation.
            const ownedRequest = config as AccessRequest;
            applyClientDeclarationHeaders(config.headers);
            if (!ownedRequest._accessTicket) ownedRequest._accessTicket = captureAccessTicket();
            if (!accessTicketIsCurrent(ownedRequest._accessTicket))
                throw new Error("Project access changed before this request");
            if (!stillSendsAsItsAccount(ownedRequest)) throw new Error(ANOTHER_ACCOUNT_SIGNED_IN);
            const nativeConfig = config as NativeOfflineRequestConfig;
            const { auth, prefs, offline } = getPlatform();
            if (nativeApiUrl(config) && nativeConfig._nativeOfflineGeneration === undefined) {
                nativeConfig._nativeOfflineGeneration = prefs.captureGeneration();
                nativeConfig._nativeOfflineCacheKey = nativeGetCacheKey(config);
            }
            // A local mirror hit resolves this request immediately. The answer-first
            // implementation keeps the ownership fences. Protected projects answer
            // too (D8): the native layer purges its sync tables on authority change.
            if (offline?.answer && offlineAccessAllowed() && !isStaffProjectRouted(config)) {
                // Per-request guard for callers that set withCredentials explicitly.
                config.withCredentials = auth.withCredentials;
                await offline.answer.tryAnswerFirst(nativeConfig, nativeApiUrl(config), axios);
            }
            const scoped = isTenantScopeRequired();
            const bootstrap = isSourceCredentialRequest(config.url);
            const admissionRequest = isUnvalidatedTenantRequestAllowed(config.url);
            const scopedConfig = config as typeof config & { _tenantGeneration?: number };
            if (scoped) {
                scopedConfig._tenantGeneration ??= captureTenantScopeGeneration();
                assertTenantRequestScope(config.url, scopedConfig._tenantGeneration);
            }
            const sessionJwt = await getEmbedSessionJwt(bootstrap);
            if (!stillSendsAsItsAccount(ownedRequest)) throw new Error(ANOTHER_ACCOUNT_SIGNED_IN);
            if (!scoped && isTenantScopeRequired())
                throw new Error("Project scope changed while authentication initialized");
            if (scoped) {
                assertTenantRequestScope(config.url, scopedConfig._tenantGeneration!);
                // The URL axios itself will send (its `baseURL`, `allowAbsoluteUrls` and params
                // rules), resolved as the browser resolves a relative one.
                assertApiOrigin(new URL(axios.getUri(config), window.location.href));
                config.headers = AxiosHeaders.from(config.headers);
                if (
                    needsMemberMutationKey(config.url, config.method) &&
                    !config.headers.has("Idempotency-Key")
                )
                    config.headers.set("Idempotency-Key", newMutationKey());
                applyTenantHeaderPolicy(config.headers, admissionRequest);
                if (!admissionRequest) {
                    // Compose from the caller's own signal, kept on the config: a resend (the 401
                    // and 503 retries below) must not compose from the previous attempt's
                    // composite, which the rejection handler has already released.
                    const owned = config as typeof config & { _callerSignal?: AbortSignal | null };
                    if (owned._callerSignal === undefined)
                        owned._callerSignal = (config.signal as AbortSignal | undefined) ?? null;
                    config.signal = scopeRequestSignal(owned._callerSignal);
                }
                config.withCredentials = !sessionJwt && bootstrap;
            }

            const projectRoute = (config as StaffProjectRouteConfig)._projectRoute;
            if (projectRoute) {
                // The API's own origin, which a native shell serves from a different host than the page.
                const api = new URL(
                    config.baseURL || window.location.origin,
                    window.location.origin,
                );
                const url = new URL(config.url ?? "", api);
                if (url.origin !== api.origin)
                    throw new Error("Cross-origin project credentials are forbidden");
                config.headers = AxiosHeaders.from(config.headers);
                applyStaffProjectRoute(config.headers, projectRoute);
            }

            if (sessionJwt) {
                config.headers = config.headers || {};
                config.headers.Authorization = `Bearer ${sessionJwt}`;
                (config as typeof config & { _usedEmbedAuth?: boolean })._usedEmbedAuth = true;
            }

            return config;
        },
        (error) => {
            redactAuthFromRejectedError(error);
            return Promise.reject(error);
        },
    );

    // One-shot guard: when a cookie session expires, every polling query 401s at
    // once; only the first should run the logout + redirect.
    let sessionExpiredHandled = false;

    async function redirectToLoginOnSessionExpiry(): Promise<void> {
        if (sessionExpiredHandled || isOwnAuthFlowPage()) {
            return;
        }
        sessionExpiredHandled = true;
        if (isDebugEnabled())
            console.log("[axios] Session expired (401) — redirecting to /authenticate");
        await performLogout();
        // After performLogout — it clears this key. Storage failures are handled internally.
        storeAuthReturn(window.location.pathname + window.location.search + window.location.hash);
        window.location.href = "/authenticate";
    }

    // Response interceptor: Handle 401 errors (expired/invalid tokens)
    axios.interceptors.response.use(
        (response) => {
            // The request has settled and Axios has already read its body, so the
            // scope signal's hold on it is done. On engines without
            // `AbortSignal.any` this is what stops a polling page from adding one
            // listener per completed request to the long-lived scope signal.
            releaseScopeRequestSignal(response.config.signal as AbortSignal | undefined);
            const captured = (
                response.config as typeof response.config & { _tenantGeneration?: number }
            )._tenantGeneration;
            if (captured !== undefined) assertTenantRequestScope(response.config.url, captured);
            const ticket = (response.config as AccessRequest)._accessTicket;
            if (ticket && !(response.config as AccessRequest)._detached)
                observeResponseAdmission(
                    ticket,
                    (name) => response.headers[name],
                    response.status,
                    String(response.config.url).endsWith("/project-access/me"),
                );
            // A local answer-first hit never touched the network -- it must not be
            // read as evidence the network just became reachable (nativeAnswerFirst.ts's
            // own background revalidation request is a normal, unguarded response here,
            // and reports "reachable" correctly when it actually succeeds).
            const { prefs, offline } = getPlatform();
            if (
                nativeApiUrl(response.config) &&
                !(offline?.answer?.isLocalAnswerResponse(response) ?? false)
            ) {
                publishConnectivityObservation("reachable");
            }
            const nativeConfig = response.config as NativeOfflineRequestConfig;
            const key = nativeConfig._nativeOfflineCacheKey;
            const generation = nativeConfig._nativeOfflineGeneration;
            if (
                key &&
                (!ticket || accessTicketIsCurrent(ticket)) &&
                generation !== undefined &&
                prefs.isGenerationCurrent(generation)
            ) {
                void prefs.write(key, response.data, generation);
            }
            return response;
        },
        async (error) => {
            releaseScopeRequestSignal(error.config?.signal as AbortSignal | undefined);
            // First, before any early return: a floored iOS build shows the native Update
            // Primepoint screen. It is not an access decision, so access and offline files stay.
            forwardAppUpdate(
                error.response?.status,
                (name) => error.response?.headers?.[name],
                error.response?.data,
            );
            if ((error.config as AccessRequest | undefined)?._detached) {
                redactAuthFromRejectedError(error);
                return Promise.reject(error);
            }
            const ticket = (error.config as AccessRequest | undefined)?._accessTicket;
            if (ticket && !accessTicketIsCurrent(ticket)) {
                redactAuthFromRejectedError(error);
                return Promise.reject(error);
            }
            // Rejections are authority too. A revoked principal normally receives
            // 403, so processing success responses alone leaves old content alive.
            const observeRejection = () => {
                try {
                    observeResponseAdmission(
                        ticket!,
                        (name) => error.response?.headers?.[name],
                        error.response?.status,
                        String(error.config?.url).endsWith("/project-access/me"),
                    );
                } catch {
                    // The request already failed. Preserve its HTTP status for
                    // the existing bounded authentication-recovery path below.
                    // The observer has fenced any revoked cached authority.
                }
            };
            // A first 401 on a recoverable embedded/native bearer is an expired
            // credential, not a definitive access denial (that carries
            // `x-access-admission: denied`). Fencing access now would advance the
            // epoch and make the verified retry fail on its stale ticket, so the
            // observation waits until recovery has had its chance.
            const config401 = error.config as
                (typeof error.config & { _usedEmbedAuth?: boolean; _retry?: boolean }) | undefined;
            const deferRejection =
                error.response?.status === 401 &&
                Boolean(config401?._usedEmbedAuth) &&
                !config401?._retry &&
                error.response?.headers?.["x-access-admission"] !== "denied";
            if (ticket && accessTicketIsCurrent(ticket) && !deferRejection) observeRejection();
            const originalRequest = error.config as typeof error.config & {
                _usedEmbedAuth?: boolean;
                _retry?: boolean;
                _safe503Retried?: boolean;
                _callerSignal?: AbortSignal | null;
                _nativeOfflineCacheKey?: string | null;
                _nativeOfflineGeneration?: number;
            };
            const { prefs, offline } = getPlatform();

            const captured = originalRequest?._tenantGeneration;
            if (captured !== undefined) {
                assertTenantRequestScope(originalRequest.url, captured);
                if (isUnvalidatedTenantRequestAllowed(originalRequest.url))
                    return Promise.reject(error);
            }

            // A staff page's request to another project's route (PP-1230) is refused for that project alone:
            // its row shows the refusal; this document's own scope, archive state and URL are untouched.
            const staffRoute = !!(originalRequest as StaffProjectRouteConfig | undefined)
                ?._projectRoute;
            if (
                !staffRoute &&
                error.response?.status === 409 &&
                error.response?.data?.detail === "project_adoption_requires_readmission"
            ) {
                closeTenantScopeForDocument();
                window.location.reload();
                return Promise.reject(error);
            }
            const refusal = staffRoute ? null : projectRefusalOf(error);
            if (refusal) markProjectRefused(refusal);
            // A rolled-back project URL: company-session decides where the tab goes. A staff
            // request routed to another project leaves this tab's own route alone.
            if (!staffRoute && isRouteDisabled(error)) renewForDisabledRoute();

            // One retry of a read the server says will clear on its own (see isSafeRetryable503).
            if (originalRequest && isSafeRetryable503(error) && !originalRequest._safe503Retried) {
                originalRequest._safe503Retried = true;
                // The caller's own signal: a scoped request's config.signal is the released composite.
                const caller =
                    originalRequest._callerSignal !== undefined
                        ? originalRequest._callerSignal
                        : (originalRequest.signal as AbortSignal | undefined);
                const retryAfter: unknown = error.response?.headers?.["retry-after"];
                await abortableDelay(safeRetryDelayMs(retryAfter), caller);
                if (caller?.aborted) {
                    // A final rejection like any other: never hand back the live bearer.
                    const canceled = new CanceledError(undefined, undefined, originalRequest);
                    redactAuthFromRejectedError(canceled);
                    return Promise.reject(canceled);
                }
                return axios(originalRequest);
            }

            // If we get 401 Unauthorized and we're using embed tokens
            const usedEmbedAuth = Boolean(originalRequest?._usedEmbedAuth);

            if (error.response?.status === 401 && usedEmbedAuth) {
                const alreadyRetried = Boolean(originalRequest._retry);
                const shouldRetry = await recoverFromEmbedAuthFailure(alreadyRetried);
                const owned = originalRequest as AccessRequest;
                const before = owned._accessTicket;
                // Retry only for the identity that sent the request: the recaptured
                // ticket must be in the same scope and any admitted lease still active
                // (another request may have been definitively denied meanwhile).
                const now = captureAccessTicket();
                const lease = resourceAccessState();
                const sameIdentity =
                    !before || (before.scope === now.scope && (!lease || lease.active));

                if (shouldRetry && !alreadyRetried && sameIdentity) {
                    originalRequest._retry = true;
                    if (before) owned._accessTicket = now;
                    return axios(originalRequest);
                }
                // Recovery declined or the identity changed: the credential failure is
                // now a real access loss, so fence access as the deferred observer would.
                if (deferRejection && ticket && accessTicketIsCurrent(ticket)) observeRejection();
            }

            // Cookie-session 401 (normal web usage): the session is gone and no
            // retry can succeed — send the user to login instead of letting the
            // app fail silently on every request.
            if (error.response?.status === 401 && !usedEmbedAuth && !isEmbedAuthEnabled) {
                await redirectToLoginOnSessionExpiry();
            }

            const isAvailabilityFailure = shouldUseOfflineFallback(error);
            const nativeUrl = originalRequest ? nativeApiUrl(originalRequest) : null;
            if (nativeUrl && isAvailabilityFailure) {
                const observation = error.response ? "serverFailure" : "transportFailure";
                publishConnectivityObservation(observation);
            }
            const canUseOfflineFallback = isAvailabilityFailure && offlineAccessAllowed();

            // "Answer first": the native DAO sync cache (desktop's offline_answer command) can
            // serve a document/section-group family this viewer has never actually opened
            // before, unlike the generic replay below, which can only echo back the exact
            // request/response pair it already saw succeed once. Tried first for that reason;
            // falls through to the generic replay when it has nothing (see
            // nativeOfflineAnswers.ts's own doc for the full three-outcome contract).
            if (nativeUrl && canUseOfflineFallback && offline?.answer) {
                const answer = await offline.answer.answer({
                    method: originalRequest.method ?? "get",
                    path: nativeUrl.pathname,
                    query: originalRequest.params,
                });
                if (answer !== null) {
                    return {
                        data: answer,
                        status: 200,
                        statusText: "OK (offline answer)",
                        headers: new AxiosHeaders({ "x-primepoint-offline-answer": "true" }),
                        config: originalRequest,
                        request: error.request,
                    } satisfies AxiosResponse;
                }
            }

            const cacheKey = originalRequest?._nativeOfflineCacheKey;
            const cacheGeneration = originalRequest?._nativeOfflineGeneration;
            if (cacheKey && cacheGeneration !== undefined && canUseOfflineFallback) {
                const cached = await prefs.read<unknown>(cacheKey, cacheGeneration);
                if (
                    cached !== null &&
                    cached !== undefined &&
                    prefs.isGenerationCurrent(cacheGeneration)
                ) {
                    return {
                        data: cached,
                        status: 200,
                        statusText: "OK (offline cache)",
                        headers: new AxiosHeaders({ "x-primepoint-offline-cache": "true" }),
                        config: originalRequest,
                        request: error.request,
                    } satisfies AxiosResponse;
                }
            }
            redactAuthFromRejectedError(error);
            return Promise.reject(error);
        },
    );
}

export default axios;
